import { Link } from 'react-router-dom';
import { LandingShell } from './LandingShell';
import {
  ClientGalleryIcon,
  PersonalPortfolioIcon,
  TalentCollabIcon,
  SubmissionPacketIcon,
} from './UseCaseIcons';

const REQUEST_ACCESS_MAILTO =
  'mailto:henrik@hallengrens.com?subject=Vuelette%20%E2%80%93%20Request%20access%20(photographer)';

export default function LandingPhotographers() {
  return (
    <LandingShell currentPage="photographers">
      <header className="vl-ap-hero">
        <div
          className="vl-hero-image-bg"
          style={{ backgroundImage: 'url(/landing-img/hero-photographer-v6.jpg)' }}
          aria-hidden
        />
        <div className="vl-ap-hero-inner">
          <div className="vl-ap-hero-text">
            <Link to="/" className="vl-back-link">← For everyone</Link>
            <div className="vl-section-eyebrow">For photographers · 02</div>
            <h1>
              From the desk <em>to the delivery</em> — without leaving the room.
            </h1>
            <p className="vl-lead">
              You shoot. You cull. You rate. You select. You deliver. Most of
              your day is moving images between five different tools, none of
              which talk to each other. Vuelette is the single quiet place
              between shutter and submission.
            </p>
          </div>
        </div>
      </header>

      <section className="vl-section">
        <div className="vl-container">
          <div className="vl-split vl-split-left">
            <div className="vl-split-image">
              <img src="/landing-img/photographer-workflow-v5.jpg" alt="" />
            </div>
            <div className="vl-split-text">
              <div className="vl-section-eyebrow">Your workflow, simplified</div>
              <h2 className="vl-section-title">
                The work you do <em>between</em> shoots, finally with a home.
              </h2>
              <p className="vl-section-lead">
                Lightroom for the raw work, Vuelette for everything after.
                Import a culled session, work through it with the people
                who matter, and deliver portfolios that don't go stale.
              </p>
              <ul className="vl-features">
                <li>
                  <span className="vl-feat-icon">⌘</span>
                  <div>
                    <strong>Side-by-side selection</strong>
                    <span>Rate frames 1–5, favorite the strongest, leave notes on individual images. Your collaborators do the same in parallel — without overwriting your view.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⌷</span>
                  <div>
                    <strong>One delivery, infinite recipients</strong>
                    <span>Build a portfolio from your best Sets. Send a single link to clients, agents, or art directors. Update once — every link updates.</span>
                  </div>
                </li>
                <li>
                  <span className="vl-feat-icon">⊞</span>
                  <div>
                    <strong>Share with your subject</strong>
                    <span>Hand a session over to the model or creator who's in it. They keep their own copy, with their own metadata, in their own account.</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="vl-section vl-section-tinted">
        <div className="vl-container">
          <div className="vl-section-eyebrow">Use cases</div>
          <h2 className="vl-section-title">
            What photographers actually use Vuelette <em>for.</em>
          </h2>

          <div className="vl-usecase-grid">
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><ClientGalleryIcon /></div>
              <span className="vl-badge">Client gallery, evolved</span>
              <h4>Not just a gallery — a working surface.</h4>
              <p>
                Most "client galleries" let clients click hearts. Vuelette
                lets you both annotate frames, build curated subsets, and
                deliver final selects without spinning up Dropbox links.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><PersonalPortfolioIcon /></div>
              <span className="vl-badge">Personal portfolio</span>
              <h4>A book that updates itself.</h4>
              <p>
                Maintain a personal portfolio that automatically reflects
                your latest editorial work. Send the same URL to every
                potential client — they always see your current best.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><TalentCollabIcon /></div>
              <span className="vl-badge">Talent collaboration</span>
              <h4>Give the model her copy.</h4>
              <p>
                Build a relationship that lasts beyond a single shoot.
                Models remember photographers who hand them tools, not
                just files. Your portfolio of talent grows with you.
              </p>
            </div>
            <div className="vl-usecase">
              <div className="vl-usecase-icon"><SubmissionPacketIcon /></div>
              <span className="vl-badge">Submission packets</span>
              <h4>Editorial submissions, properly framed.</h4>
              <p>
                Sending a story to a magazine? Build a primary set of the
                hero frames and a secondary set of context shots. Add
                captions. Send a link, not a 600 MB folder.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="vl-cta-block">
        <div className="vl-container">
          <h2 className="vl-section-title" style={{ marginInline: 'auto' }}>
            Spend less time on logistics, <em>more on light.</em>
          </h2>
          <a href={REQUEST_ACCESS_MAILTO} className="vl-btn vl-btn-primary">
            Request access →
          </a>
          <p style={{ marginTop: '1.5rem' }}>
            <Link to="/" style={{ color: 'var(--vl-text-3)' }}>← Back to hub</Link>
          </p>
        </div>
      </section>
    </LandingShell>
  );
}
